'use client';

import { Button } from '@balder/ui/components/button';
import { Input } from '@balder/ui/components/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@balder/ui/components/input-otp';
import { Label } from '@balder/ui/components/label';
import { useAuth, useSignIn } from '@clerk/nextjs';
import { isClerkAPIResponseError } from '@clerk/nextjs/errors';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { z } from 'zod';

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18A10.96 10.96 0 0 0 1 12c0 1.77.43 3.45 1.18 4.94l3.66-2.84z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
      />
    </svg>
  );
}

// Clerk's 429s carry an empty errors[] — the reactive errors.fields/errors.global render
// nothing for them, so rate limits must be surfaced explicitly.
const RATE_LIMIT_MESSAGE = 'Too many attempts. Wait a minute and try again.';

function isRateLimited(error: unknown): boolean {
  return isClerkAPIResponseError(error) && error.status === 429;
}

function isNotFound(error: unknown): boolean {
  return isClerkAPIResponseError(error) && error.errors[0]?.code === 'form_identifier_not_found';
}

// Only follow same-origin redirect targets (middleware adds redirect_url on auth.protect()).
function safeRedirect(redirectUrl: string | undefined): string {
  if (!redirectUrl) return '/';
  try {
    const url = new URL(redirectUrl, window.location.origin);
    return url.origin === window.location.origin ? url.pathname + url.search + url.hash : '/';
  } catch {
    return '/';
  }
}

// Custom sign-in matching the Clerk instance config exactly: Google OAuth or
// email + email-code. No password first factor, no 2FA, no client trust
// (verified against /v1/environment on clerk.balder.co — revisit if strategies change).
// Follows Clerk's custom-flow docs: field/global errors come from the reactive
// `errors` on useSignIn(); the per-call `{ error }` results only drive flow control.
function SigningIn() {
  return (
    <div className="flex w-full flex-col items-center gap-4 py-16" role="status" aria-live="polite">
      <div className="border-muted-foreground/30 border-t-foreground size-6 animate-spin rounded-full border-2" />
      <p className="text-muted-foreground text-sm">Signing you in…</p>
    </div>
  );
}

export function SignInForm({ redirectUrl }: { redirectUrl?: string }) {
  const { signIn, errors, fetchStatus } = useSignIn();
  const { isSignedIn, isLoaded } = useAuth();
  const router = useRouter();

  const [step, setStep] = useState<'start' | 'code'>('start');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [resent, setResent] = useState(false);
  // Catches typos the browser's type="email" allows, e.g. a missing TLD ("name@company")
  const [emailFormatError, setEmailFormatError] = useState(false);
  // Only for failures Clerk's reactive errors can't express (finalize, unexpected status)
  const [localError, setLocalError] = useState<string | null>(null);
  // Verified successfully — show the loading view until navigation lands in the app
  const [finalizing, setFinalizing] = useState(false);
  // Google redirect in flight — instant feedback for the FAPI roundtrip before navigation
  const [googlePending, setGooglePending] = useState(false);

  // The post-sign-in navigation can race session-cookie propagation: middleware may bounce
  // one request back to a fresh /sign-in render even though the client session is active.
  // Instead of flashing the form at an authenticated user, show the loader and escape.
  useEffect(() => {
    if (isSignedIn && !finalizing) {
      router.replace(safeRedirect(redirectUrl));
    }
  }, [isSignedIn, finalizing, redirectUrl, router]);

  const busy = fetchStatus === 'fetching';
  // Clerk calls silently no-op before clerk-js has loaded — gate the actions on
  // isLoaded (same pattern as Clerk's own examples) so a fast click can't be lost.
  const disabled = busy || !isLoaded;
  const identifierError = errors.fields.identifier;
  const codeError = errors.fields.code;
  // Note: errors.global is intentionally NOT rendered — it carries raw Clerk internals
  // (e.g. "Email code factor not found") that both look broken and re-leak enumeration for
  // unknown emails. User-facing failures go through localError / field errors instead.

  const finalize = async () => {
    const { error } = await signIn.finalize({
      navigate: ({ session, decorateUrl }) => {
        // Session tasks (e.g. choose-org) are unused here — onboarding is enforced by proxy.ts
        if (session?.currentTask) {
          router.push('/');
          return;
        }
        const url = decorateUrl(safeRedirect(redirectUrl));
        if (url.startsWith('http')) {
          window.location.href = url;
        } else {
          router.push(url);
        }
      },
    });
    if (error) {
      setFinalizing(false);
      setLocalError('Could not complete the sign-in. Try again.');
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    setEmailFormatError(false);

    if (!z.email().safeParse(email).success) {
      setEmailFormatError(true);
      return;
    }

    // Enumeration-neutral: an unknown email must look identical to a real one, so we
    // advance to the code step for every non-rate-limited outcome. With Clerk's
    // enumeration protection enabled, create()/sendCode() succeed for unknown emails and
    // this is the happy path; with it off they reject with a not-found 422 — caught below
    // and treated the same. Either way the response is uniform; a non-user gets no code.
    // (create/sendCode inconsistently return `{ error }` vs throw, so both are handled.)
    try {
      const { error: createError } = await signIn.create({ identifier: email });
      if (createError) {
        if (isRateLimited(createError)) {
          setLocalError(RATE_LIMIT_MESSAGE);
          return;
        }
        if (!isNotFound(createError)) return; // genuine input error → reactive errors.fields
      } else {
        const { error: sendError } = await signIn.emailCode.sendCode();
        if (sendError && isRateLimited(sendError)) {
          setLocalError(RATE_LIMIT_MESSAGE);
          return;
        }
      }
    } catch (err) {
      if (isRateLimited(err)) {
        setLocalError(RATE_LIMIT_MESSAGE);
        return;
      }
      // not-found / transient — fall through to the neutral code step
    }

    setStep('code');
  };

  const verifyCode = async (value: string) => {
    setLocalError(null);

    try {
      const { error } = await signIn.emailCode.verifyCode({ code: value });
      if (error) {
        if (isRateLimited(error)) setLocalError(RATE_LIMIT_MESSAGE);
        return; // wrong/expired code → errors.fields.code renders a clean message
      }
    } catch (err) {
      // Unknown email has no factor → verifyCode rejects with factor_not_found. Stay
      // neutral (no raw error), just as sendCode does; only rate limits surface.
      if (isRateLimited(err)) setLocalError(RATE_LIMIT_MESSAGE);
      return;
    }

    if (signIn.status === 'complete') {
      setFinalizing(true);
      await finalize();
    } else {
      // needs_second_factor / needs_client_trust — not enabled on this instance
      setLocalError('Something went wrong. Try again or contact us at balder.co/contact.');
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await verifyCode(code);
  };

  const handleGoogle = async () => {
    setLocalError(null);
    setGooglePending(true);
    // Success navigates the whole page away (pending state stays until then). Both a
    // returned error and a thrown one (network mid-flight) would otherwise be silent.
    try {
      const { error } = await signIn.sso({
        strategy: 'oauth_google',
        redirectUrl: safeRedirect(redirectUrl),
        redirectCallbackUrl: '/sign-in/sso-callback',
      });
      if (error) {
        setGooglePending(false);
        setLocalError(
          isRateLimited(error) ? RATE_LIMIT_MESSAGE : 'Google sign-in failed. Try again.',
        );
      }
    } catch {
      setGooglePending(false);
      setLocalError('Google sign-in failed. Try again.');
    }
  };

  const handleResend = async () => {
    setLocalError(null);
    setResent(false);
    // Enumeration-neutral, mirroring handleEmailSubmit: an unknown email has no email-code
    // factor, so sendCode rejects with factor_not_found — swallow it and show "Code sent."
    // regardless, so a resend looks identical whether or not a code actually goes out.
    try {
      const { error } = await signIn.emailCode.sendCode();
      if (error && isRateLimited(error)) {
        setLocalError(RATE_LIMIT_MESSAGE);
        return;
      }
    } catch (err) {
      if (isRateLimited(err)) {
        setLocalError(RATE_LIMIT_MESSAGE);
        return;
      }
    }
    setResent(true);
  };

  const handleBack = async () => {
    setStep('start');
    setCode('');
    setResent(false);
    setLocalError(null);
    // Clear the pending sign-in attempt (Clerk's "start over" pattern)
    await signIn.reset();
  };

  if (finalizing || isSignedIn) {
    return <SigningIn />;
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {step === 'code' ? (
        <>
          <button
            type="button"
            onClick={handleBack}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 self-start text-sm"
          >
            <ArrowLeft className="size-4" aria-hidden />
            Back
          </button>
          <div>
            <h1 className="font-heading text-2xl font-semibold leading-tight text-balder-black">
              Check your email
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              If you have a Balder account, we&apos;ve sent a code to{' '}
              <span className="font-medium text-foreground">{email}</span>.
            </p>
          </div>
          <form onSubmit={handleCodeSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="code">Code</Label>
              <InputOTP
                id="code"
                maxLength={6}
                pattern={'^\\d+$'} // digits only (input-otp's REGEXP_ONLY_DIGITS; dep lives in @balder/ui)
                autoFocus
                value={code}
                onChange={setCode}
                onComplete={verifyCode}
                disabled={busy}
                aria-invalid={!!codeError}
                aria-describedby={codeError ? 'code-error' : undefined}
              >
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <InputOTPSlot key={index} index={index} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {codeError && (
                <p id="code-error" role="alert" className="text-destructive text-sm">
                  {codeError.longMessage ?? codeError.message}
                </p>
              )}
            </div>
            {localError && (
              <p role="alert" className="text-destructive text-sm">
                {localError}
              </p>
            )}
            <Button
              type="submit"
              disabled={disabled || code.length < 6}
              className="rounded-lg font-semibold"
            >
              {busy ? 'Verifying…' : 'Verify'}
            </Button>
          </form>
          <p className="text-muted-foreground text-sm" aria-live="polite">
            {resent ? (
              'Code sent.'
            ) : (
              <>
                Didn&apos;t get it?{' '}
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={disabled}
                  className="text-foreground underline underline-offset-4"
                >
                  Resend code
                </button>
              </>
            )}
          </p>
        </>
      ) : (
        <>
          <div>
            <h1 className="font-heading whitespace-nowrap text-2xl font-semibold leading-tight text-balder-black">
              Log in to Balder Cloud
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your work email and we&apos;ll send you a code.
            </p>
          </div>
          <Button
            variant="outline"
            type="button"
            onClick={handleGoogle}
            disabled={disabled || googlePending}
            className="rounded-lg font-semibold"
          >
            {googlePending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Redirecting to Google…
              </>
            ) : (
              <>
                <GoogleIcon />
                Continue with Google
              </>
            )}
          </Button>
          <p className="flex items-center gap-3 text-xs text-muted-foreground before:h-px before:flex-1 before:bg-border after:h-px after:flex-1 after:bg-border">
            or
          </p>
          <form onSubmit={handleEmailSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-sm font-semibold text-foreground">
                Email
              </Label>
              <div className="flex gap-2.5">
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  // text-sm also on mobile: the shadcn default is 16px there (iOS anti-zoom),
                  // but this app already prevents input zoom via viewport maximumScale=1
                  className="flex-1 rounded-lg border-border bg-muted/50 text-sm"
                  aria-invalid={!!identifierError || emailFormatError}
                  aria-describedby={identifierError || emailFormatError ? 'email-error' : undefined}
                />
                <Button
                  type="submit"
                  disabled={disabled || !email}
                  className="rounded-lg font-semibold"
                >
                  {busy ? 'Sending…' : 'Continue'}
                </Button>
              </div>
              {emailFormatError ? (
                <p id="email-error" role="alert" className="text-destructive text-sm">
                  Enter a valid email address.
                </p>
              ) : (
                // Never surface "no account for that email" here — that would re-open the
                // user-enumeration oracle the neutral flow is closing. Genuine format/input
                // errors only.
                identifierError && (
                  <p id="email-error" role="alert" className="text-destructive text-sm">
                    {identifierError.longMessage ?? identifierError.message}
                  </p>
                )
              )}
            </div>
            {localError && (
              <p role="alert" className="text-destructive text-sm">
                {localError}
              </p>
            )}
          </form>
        </>
      )}
    </div>
  );
}
